import { useEffect, useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { RootState } from "store/store";
import dayjs from "dayjs";
import { Empty, List, Spin, Typography } from "antd";
import { VideoCameraOutlined } from "@ant-design/icons";
import ActivityLogVideoModal from "./ActivityLogVideoModal";
import { useTranslation } from "react-i18next";

const ActivityLogTab: React.FC = () => {
  const { t } = useTranslation();

  const orgId = useSelector((state: RootState) => state.globalOrg.globalOrgId);
  const { selectedVisit, selectedVisitor } = useSelector(
    (state: RootState) => state.visits,
  );
  const { videoDevices, devicesByWorkflow } = useSelector(
    (state: RootState) => state.altaVideo,
  );

  const [logs, setLogs] = useState<any[] | null>(null);
  const [selectedLogType, setSeletedLogType] = useState<
    "SIGNIN" | "SIGNOUT" | null
  >(null);
  const selectedVisitSiteId = selectedVisit?.site?.id ?? null;
  const devicesByWorkflowForSite = selectedVisitSiteId
    ? devicesByWorkflow[selectedVisitSiteId]
    : null;

  const mergedSignInDeviceIds = useMemo(() => {
    const attendantIds = devicesByWorkflowForSite?.signInDeviceId ?? [];
    const kioskIds = devicesByWorkflowForSite?.kioskInDeviceId ?? [];
    return Array.from(new Set([...attendantIds, ...kioskIds]));
  }, [
    devicesByWorkflowForSite?.signInDeviceId,
    devicesByWorkflowForSite?.kioskInDeviceId,
  ]);

  const mergedSignOutDeviceIds = useMemo(() => {
    const attendantIds = devicesByWorkflowForSite?.signOutDeviceId ?? [];
    const kioskIds = devicesByWorkflowForSite?.kioskOutDeviceId ?? [];
    return Array.from(new Set([...attendantIds, ...kioskIds]));
  }, [
    devicesByWorkflowForSite?.signOutDeviceId,
    devicesByWorkflowForSite?.kioskOutDeviceId,
  ]);

  useEffect(() => {
    if (!selectedVisitor) {
      setLogs([]);
      return;
    }

    const tempLogs: any[] = [];

    const hasVideo = (targetIds: string[]) =>
      targetIds.some((id) => videoDevices.some((v) => v.guId === id));

    // Denied Entry / Signed In
    if (selectedVisitor.signIn) {
      if (hasVideo(mergedSignInDeviceIds)) {
        tempLogs.push({
          id: "signIn",
          time: selectedVisitor.signIn,
          type: selectedVisitor.status === 99 ? "Denied" : "Sign In",
          video: true,
        });
      }
    }

    // Signed Out
    if (selectedVisitor.signOut && hasVideo(mergedSignOutDeviceIds)) {
      tempLogs.push({
        id: "signOut",
        time: selectedVisitor.signOut,
        type: "Sign Out",
        video: true,
      });
    }

    setLogs(tempLogs);
  }, [
    orgId,
    selectedVisitor,
    videoDevices,
    mergedSignInDeviceIds,
    mergedSignOutDeviceIds,
  ]);

  if (logs === null) return <Spin />;

  return (
    <>
      <List
        header={
          <Typography.Text>
            {t("visitors.profile.videoLog.title")}
          </Typography.Text>
        }
        bordered
        dataSource={logs}
        locale={{
          emptyText: <Empty description={t("common.noData")} />,
        }}
        renderItem={(log) => (
          <List.Item key={log.id}>
            <div
              style={{
                width: "100%",
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <span>{dayjs(log.time).format("YYYY-MM-DD hh:mm A")}</span>
              <span
                style={{
                  color:
                    log.type === "Sign Out"
                      ? "#999"
                      : log.type === "Denied"
                        ? "red"
                        : "green",
                }}
              >
                {log.type}
              </span>
              {log.video && (
                <VideoCameraOutlined
                  onClick={() =>
                    setSeletedLogType(
                      log.id === "signOut" ? "SIGNOUT" : "SIGNIN",
                    )
                  }
                  style={{ cursor: "pointer", marginLeft: 8 }}
                />
              )}
            </div>
          </List.Item>
        )}
      />

      {selectedLogType && devicesByWorkflowForSite && (
        <ActivityLogVideoModal
          open={!!selectedLogType}
          onClose={() => setSeletedLogType(null)}
          targetType={selectedLogType}
          videoDevices={videoDevices}
          devicesByWorkflow={devicesByWorkflowForSite}
          selectedDeviceGuIds={
            selectedLogType === "SIGNIN"
              ? mergedSignInDeviceIds
              : mergedSignOutDeviceIds
          }
        />
      )}
    </>
  );
};

export default ActivityLogTab;
